import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import { useIsMobile } from '../../utils/useIsMobile';

// ─────────────────────────────────────────────────────────────────────────────
// Arbol de modulos.
//
// `campo` es la columna real de la tabla `planes`, y tambien la clave que se
// guarda como excepcion del negocio: esas filas se pueden marcar. Las que traen
// `derivadoDe` reflejan otra fila y no se editan por separado, porque en la
// base no existen como permiso propio. Las que no traen ninguno de los dos van
// incluidas siempre.
//
// Aqui no se inventan permisos: si una fila no corresponde a algo que el
// sistema comprueba de verdad, no aparece.
// ─────────────────────────────────────────────────────────────────────────────
const GRUPOS = [
  {
    icono: '📅', titulo: 'Agenda',
    filas: [
      { label: 'Calendario',             campo: 'tiene_calendario',   nota: 'Agenda del día y de la semana' },
      { label: 'Citas',                  campo: 'tiene_citas',        nota: 'Listado, cambios y cancelaciones' },
      { label: 'Horarios',               campo: 'tiene_horarios',     nota: 'Horario de atención y descansos' },
    ],
  },
  {
    icono: '⚙️', titulo: 'Configuración del negocio',
    filas: [
      { label: 'Mi Negocio',             campo: 'tiene_mi_negocio',     nota: 'Datos, logo y página pública' },
      { label: 'Notificaciones',         campo: 'tiene_notificaciones', nota: 'Plantillas y conexión de WhatsApp' },
    ],
  },
  {
    icono: '👥', titulo: 'Gestión de usuarios y sedes',
    filas: [
      { label: 'Usuarios',               nota: 'Según el máximo del plan' },
      { label: 'Sedes',                  campo: 'tiene_multi_sucursal', nota: 'Varias sedes por negocio' },
      { label: 'Consolidado multi-sede', derivadoDe: 'tiene_multi_sucursal' },
    ],
  },
  {
    icono: '💼', titulo: 'Gestión de trabajadores',
    filas: [
      { label: 'Trabajadores',           campo: 'tiene_trabajadores' },
      { label: 'Descansos del personal', derivadoDe: 'tiene_trabajadores' },
    ],
  },
  {
    icono: '📊', titulo: 'Reportes',
    filas: [
      { label: 'Reportes operativos',    campo: 'tiene_reportes' },
      { label: 'Reportes avanzados',     campo: 'tiene_reportes_avanzados' },
    ],
  },
  {
    icono: '💲', titulo: 'Finanzas',
    filas: [
      { label: 'Ingresos y gastos',      campo: 'tiene_finanzas' },
      { label: 'Flujo de caja',          derivadoDe: 'tiene_finanzas' },
    ],
  },
  {
    icono: '📈', titulo: 'Dashboard',
    filas: [
      { label: 'Panel principal',        campo: 'tiene_dashboard' },
      { label: 'Métricas y KPIs',        derivadoDe: 'tiene_dashboard' },
    ],
  },
  {
    icono: '⭐', titulo: 'Incluido en todos los planes',
    filas: [
      { label: 'Página pública de reservas' },
      { label: 'Clientes' },
      { label: 'Servicios' },
      { label: 'Confirmaciones por WhatsApp' },
      { label: 'Soporte técnico' },
    ],
  },
];

const COLORES = ['#22c55e', '#3b82f6', '#4f46e5', '#8b5cf6', '#ec4899'];
const codigo = (id) => 'NEG-' + String(id).padStart(4, '0');
const activo = (v) => v === 1 || v === true || v === '1';

export default function PermissionsPage() {
  const esMovil = useIsMobile(1100);

  const [negocios, setNegocios] = useState([]);
  const [planes, setPlanes]     = useState([]);
  const [busqueda, setBusqueda] = useState('');
  const [sel, setSel]           = useState(null);
  const [modulos, setModulos]   = useState(null); // { plan, excepciones, efectivo }
  const [estado, setEstado]     = useState('');
  const [error, setError]       = useState('');
  const [aviso, setAviso]       = useState('');
  const [cargando, setCargando] = useState(true);

  const notificar = (msg, esError = false) => {
    if (esError) { setError(msg); setTimeout(() => setError(''), 5000); }
    else         { setAviso(msg); setTimeout(() => setAviso(''), 5000); }
  };

  useEffect(() => {
    (async () => {
      try {
        const [n, p] = await Promise.all([api.get('/businesses'), api.get('/plans/todos')]);
        setNegocios(n.data || []);
        setPlanes(p.data || []);
        setSel((n.data || [])[0] || null);
      } catch (err) {
        setError(err.response?.data?.error || 'No se pudieron cargar negocios y planes');
      } finally { setCargando(false); }
    })();
  }, []);

  const cargarModulos = useCallback(async (negocioId) => {
    if (!negocioId) return;
    setModulos(null);
    try {
      const r = await api.get(`/businesses/${negocioId}/modules`);
      setModulos(r.data);
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudieron cargar los módulos del negocio');
    }
  }, []);

  useEffect(() => { cargarModulos(sel?.id); }, [sel?.id, cargarModulos]);

  const cambiarPlan = async (planId) => {
    if (!sel) return;
    setEstado('guardando');
    try {
      const r = await api.patch(`/businesses/${sel.id}/plan`, { plan_id: Number(planId) });
      const nuevo = planes.find(p => p.id === Number(planId));
      setSel(s => ({ ...s, plan_id: Number(planId), plan_name: nuevo?.nombre_visible }));
      setNegocios(ns => ns.map(n => n.id === sel.id
        ? { ...n, plan_id: Number(planId), plan_name: nuevo?.nombre_visible } : n));
      setEstado('guardado');
      // El backend avisa cuando el cambio inactiva sucursales.
      if (r.data?.message && r.data.message !== 'Plan actualizado') notificar(r.data.message);
      cargarModulos(sel.id);
    } catch (err) {
      setEstado('');
      notificar(err.response?.data?.error || 'No se pudo cambiar el plan', true);
    }
  };

  // habilitado: true | false | null (null borra la excepcion)
  const guardarModulo = async (campo, habilitado) => {
    if (!sel) return;
    setEstado('guardando');
    try {
      const r = await api.put(`/businesses/${sel.id}/modules`, { modulo: campo, habilitado });
      setModulos(r.data);
      setEstado('guardado');
    } catch (err) {
      setEstado('');
      notificar(err.response?.data?.error || 'No se pudo guardar el cambio', true);
    }
  };

  const filtrados = negocios.filter(n =>
    !busqueda || String(n.nombre || n.name || '').toLowerCase().includes(busqueda.toLowerCase())
  );

  const excepciones = modulos ? Object.keys(modulos.excepciones || {}).length : 0;

  return (
    <div className="page-content">
      <div className="page-header">
        <div>
          <h1 className="page-title">Árbol de permisos por negocio</h1>
          <p className="page-subtitle">Qué módulos tiene cada negocio, y de qué plan los hereda</p>
        </div>
      </div>

      {aviso && <div className="alert alert-success" style={{ marginBottom: '1rem' }}>{aviso}</div>}
      {error && <div className="alert alert-danger"  style={{ marginBottom: '1rem' }}>{error}</div>}

      <div style={{
        display: 'grid',
        gridTemplateColumns: esMovil ? '1fr' : '280px minmax(0, 1fr)',
        gap: '1.25rem', alignItems: 'start',
      }}>
        {/* Negocios */}
        <div className="card" style={{ minWidth: 0 }}>
          <div style={{ padding: '1rem 1.15rem', borderBottom: '1px solid var(--color-border)' }}>
            <h3 style={{ margin: '0 0 10px', fontSize: '0.8rem', letterSpacing: '.04em', color: 'var(--color-text-muted)' }}>
              NEGOCIOS
            </h3>
            <input className="form-input" style={{ margin: 0 }} placeholder="Buscar negocio..."
                   value={busqueda} onChange={e => setBusqueda(e.target.value)} />
          </div>

          <div style={{ maxHeight: esMovil ? 300 : 560, overflowY: 'auto' }}>
            {cargando ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>Cargando...</div>
            ) : filtrados.length === 0 ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>Sin resultados</div>
            ) : filtrados.map(n => {
              const activoNeg = activo(n.activo ?? n.is_active ?? 1);
              return (
                <button
                  key={n.id}
                  onClick={() => setSel(n)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left',
                    padding: '0.8rem 1.15rem', border: 'none', cursor: 'pointer',
                    borderBottom: '1px solid var(--color-border)',
                    borderLeft: `3px solid ${sel?.id === n.id ? 'var(--color-gold)' : 'transparent'}`,
                    background: sel?.id === n.id ? 'rgba(79,70,229,0.08)' : 'transparent',
                  }}
                >
                  {n.url_logo
                    ? <img src={n.url_logo} alt="" style={{ width: 34, height: 34, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />
                    : <div style={{
                        width: 34, height: 34, borderRadius: 8, flexShrink: 0,
                        background: 'var(--color-surface)', display: 'flex',
                        alignItems: 'center', justifyContent: 'center', fontSize: '1rem',
                      }}>🏪</div>}
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.87rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {n.nombre || n.name}
                    </div>
                    <div style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)' }}>
                      {codigo(n.id)}
                      <span style={{ color: activoNeg ? '#22c55e' : '#ef4444' }}>
                        {'  •  '}{activoNeg ? 'Activo' : 'Suspendido'}
                      </span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          <div style={{ padding: '0.8rem 1.15rem', fontSize: '0.76rem', color: 'var(--color-text-muted)' }}>
            Mostrando {filtrados.length} de {negocios.length} negocios
          </div>
        </div>

        {/* Matriz */}
        <div className="card" style={{ minWidth: 0 }}>
          <div style={{
            padding: '1.15rem 1.25rem', borderBottom: '1px solid var(--color-border)',
            display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end',
          }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>Configurando</div>
              <div style={{ fontWeight: 700, fontSize: '1.05rem' }}>
                {sel ? (sel.nombre || sel.name) : 'Selecciona un negocio'}
              </div>
            </div>

            {sel && (
              <div style={{ minWidth: 0 }}>
                <label className="form-label" style={{ display: 'block' }}>Plan del negocio</label>
                <select className="form-select" style={{ margin: 0, maxWidth: 240 }}
                        value={sel.plan_id || ''} onChange={e => cambiarPlan(e.target.value)}>
                  <option value="" disabled>Sin plan asignado</option>
                  {planes.map(p => <option key={p.id} value={p.id}>{p.nombre_visible}</option>)}
                </select>
              </div>
            )}
          </div>

          <div style={{
            padding: '0.7rem 1.25rem', background: 'rgba(59,130,246,0.07)',
            borderBottom: '1px solid var(--color-border)', fontSize: '0.79rem', lineHeight: 1.5,
          }}>
            Los cambios en la columna <strong>Este negocio</strong> afectan solo a
            {' '}{sel ? (sel.nombre || sel.name) : 'el negocio seleccionado'}. Las columnas de
            los planes son de referencia y no se editan aquí.
            {excepciones > 0 && (
              <> Actualmente hay <strong>{excepciones} excepción{excepciones === 1 ? '' : 'es'}</strong> sobre el plan.</>
            )}
          </div>

          {/* Lo que paga este negocio al mes: el plan más los módulos que se
              le encendieron aquí. Asignar y cobrar son la misma acción. */}
          {modulos?.mensualidad && (
            <div style={{
              padding: '0.85rem 1.25rem', borderBottom: '1px solid var(--color-border)',
              background: 'var(--color-surface-2)',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--color-text-faint)' }}>
                  Mensualidad de este negocio
                </span>
                <strong style={{ fontSize: '1.15rem', letterSpacing: '-0.02em' }}>
                  ${modulos.mensualidad.total.toLocaleString('es-CO')}
                </strong>
              </div>
              <div style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)', marginTop: 3 }}>
                Plan ${modulos.mensualidad.plan.toLocaleString('es-CO')}
                {modulos.mensualidad.lineas.length > 0 ? (
                  <> + {modulos.mensualidad.lineas.map(l => `${l.nombre} $${l.precio.toLocaleString('es-CO')}`).join(' + ')}</>
                ) : ' · sin servicios adicionales'}
              </div>
            </div>
          )}

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', minWidth: 620, borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{
                    textAlign: 'left', padding: '0.9rem 1.25rem', fontSize: '0.76rem',
                    letterSpacing: '.04em', color: 'var(--color-text-muted)',
                    borderBottom: '1px solid var(--color-border)',
                  }}>MÓDULOS / SERVICIOS</th>

                  <th style={{
                    padding: '0.9rem 0.6rem', textAlign: 'center', minWidth: 150,
                    borderBottom: '1px solid var(--color-border)',
                    background: 'rgba(79,70,229,0.08)',
                  }}>
                    <div style={{ fontWeight: 700, fontSize: '0.88rem', color: 'var(--color-gold)' }}>
                      Este negocio
                    </div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)' }}>
                      {sel?.plan_name ? `Hereda de ${sel.plan_name}` : 'Sin plan'}
                    </div>
                  </th>

                  {planes.map((p, i) => (
                    <th key={p.id} style={{
                      padding: '0.9rem 0.6rem', textAlign: 'center', minWidth: 130,
                      borderBottom: '1px solid var(--color-border)',
                    }}>
                      <div style={{ fontWeight: 700, fontSize: '0.85rem', color: COLORES[i % COLORES.length] }}>
                        ● {p.nombre_visible}
                      </div>
                      {sel?.plan_id === p.id && (
                        <div style={{ marginTop: 4 }}><Chip texto="Asignado" color="#4f46e5" /></div>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {GRUPOS.map(grupo => (
                  <React.Fragment key={grupo.titulo}>
                    <tr>
                      <td colSpan={planes.length + 2} style={{
                        padding: '0.7rem 1.25rem', fontWeight: 700, fontSize: '0.84rem',
                        background: 'var(--color-surface)', borderBottom: '1px solid var(--color-border)',
                      }}>{grupo.icono} {grupo.titulo}</td>
                    </tr>

                    {grupo.filas.map(fila => (
                      <tr key={fila.label}>
                        <td style={{
                          padding: '0.6rem 1.25rem 0.6rem 2.5rem', fontSize: '0.85rem',
                          borderBottom: '1px solid var(--color-border)',
                        }}>
                          {fila.label}
                          {fila.nota && (
                            <div style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)' }}>{fila.nota}</div>
                          )}
                          {/* Lo que cuesta encender este módulo. Solo se cobra
                              si el plan no lo traía ya incluido. */}
                          {(() => {
                            if (!fila.campo) return null;
                            const s = modulos?.catalogo?.[fila.campo];
                            // Sin fila en el catálogo es un módulo que va
                            // dentro del plan: se puede apagar, pero no se
                            // cobra aparte.
                            if (!s || !s.precio) {
                              return (
                                <div style={{ fontSize: '0.72rem', marginTop: 2, color: 'var(--color-success)' }}>
                                  Incluido en el plan
                                </div>
                              );
                            }
                            const enElPlan = !!modulos?.plan?.[fila.campo];
                            const activo = !!modulos?.efectivo?.[fila.campo];
                            return (
                              <div style={{
                                fontSize: '0.72rem', marginTop: 2,
                                color: enElPlan ? 'var(--color-success)'
                                     : activo ? 'var(--color-text)' : 'var(--color-text-faint)',
                                fontWeight: activo && !enElPlan ? 600 : 400,
                              }}>
                                {enElPlan ? 'Incluido en el plan' : `$${s.precio.toLocaleString('es-CO')}/mes`}
                              </div>
                            );
                          })()}
                        </td>

                        <CeldaNegocio
                          fila={fila}
                          modulos={modulos}
                          onGuardar={guardarModulo}
                        />

                        {planes.map(p => <CeldaPlan key={p.id} plan={p} fila={fila} />)}
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{
            display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
            padding: '0.9rem 1.25rem', borderTop: '1px solid var(--color-border)', fontSize: '0.78rem',
          }}>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', color: 'var(--color-text-muted)', alignItems: 'center' }}>
              <span><Marca tipo="si" /> Incluido</span>
              <span><Marca tipo="no" /> No incluido</span>
              <span><Marca tipo="fijo" /> En todos los planes</span>
              <span style={{ color: '#8b5cf6' }}>★ Excepción de este negocio</span>
            </div>
            <span style={{ color: 'var(--color-text-muted)' }}>
              {estado === 'guardando' ? 'Guardando...'
                : estado === 'guardado' ? '✓ Los cambios se guardan automáticamente'
                : 'Los cambios se guardan automáticamente'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function Chip({ texto, color }) {
  return (
    <span style={{
      display: 'inline-block', padding: '1px 7px', borderRadius: 999,
      fontSize: '0.65rem', fontWeight: 700, color, background: `${color}1f`,
    }}>{texto}</span>
  );
}

function Marca({ tipo }) {
  const estilo = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 20, height: 20, borderRadius: 6, fontSize: '0.72rem', fontWeight: 700,
  };
  if (tipo === 'si') return <span style={{ ...estilo, background: 'rgba(34,197,94,0.15)', color: '#16a34a' }}>✓</span>;
  if (tipo === 'no') return <span style={{ ...estilo, background: 'rgba(239,68,68,0.13)', color: '#dc2626' }}>✕</span>;
  return <span style={{ ...estilo, background: 'var(--color-surface)', color: 'var(--color-text-muted)' }}>✓</span>;
}

/** Columna del negocio: es la unica editable. */
function CeldaNegocio({ fila, modulos, onGuardar }) {
  const fondo = { textAlign: 'center', padding: '0.6rem', borderBottom: '1px solid var(--color-border)', background: 'rgba(79,70,229,0.05)' };

  if (!modulos) return <td style={fondo}>·</td>;

  const fijo = !fila.campo && !fila.derivadoDe;
  if (fijo) return <td style={fondo}><span title="Incluido en todos los planes"><Marca tipo="fijo" /></span></td>;

  const campo = fila.campo || fila.derivadoDe;
  const valor = !!modulos.efectivo?.[campo];
  const esExcepcion = Object.prototype.hasOwnProperty.call(modulos.excepciones || {}, campo);

  // Las derivadas siguen a su modulo principal y no se tocan por separado.
  if (fila.derivadoDe) {
    return (
      <td style={fondo}>
        <span title="Depende del módulo principal del grupo" style={{ opacity: 0.6 }}>
          <Marca tipo={valor ? 'si' : 'no'} />
        </span>
      </td>
    );
  }

  return (
    <td style={fondo}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
        <button
          onClick={() => onGuardar(campo, !valor)}
          title={valor ? 'Quitar este módulo a este negocio' : 'Dar este módulo a este negocio'}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, lineHeight: 0 }}
        >
          <Marca tipo={valor ? 'si' : 'no'} />
        </button>
        {esExcepcion && (
          <button
            onClick={() => onGuardar(campo, null)}
            title="Es una excepción de este negocio. Clic para volver a lo que dice el plan."
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: 0,
              color: '#8b5cf6', fontSize: '0.8rem', lineHeight: 1,
            }}
          >★</button>
        )}
      </div>
    </td>
  );
}

/** Columnas de los planes: solo referencia. */
function CeldaPlan({ plan, fila }) {
  const estilo = { textAlign: 'center', padding: '0.6rem', borderBottom: '1px solid var(--color-border)' };
  const fijo = !fila.campo && !fila.derivadoDe;
  if (fijo) return <td style={estilo}><Marca tipo="fijo" /></td>;

  const campo = fila.campo || fila.derivadoDe;
  const valor = activo(plan[campo]);
  return (
    <td style={estilo}>
      <span style={{ opacity: fila.derivadoDe ? 0.6 : 1 }}><Marca tipo={valor ? 'si' : 'no'} /></span>
    </td>
  );
}
